import React, { useState, useEffect } from "react";
import { Bell, X } from "lucide-react";
import usePushSubscription from "../hooks/usePushSubscription";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const DISMISS_KEY = "tapin_push_prompt_dismissed_v1";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function wasDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch { return false; }
}

/**
 * PushPromptBanner — mobile-first dismissible banner that nudges signed-in
 * viewers to enable push notifications so they get pinged the moment a
 * followed creator goes live. Hides automatically when:
 *   - Browser doesn't support push
 *   - User isn't signed in
 *   - User is already subscribed OR previously denied permission
 *   - User dismissed the banner in the last 7 days
 */
export default function PushPromptBanner({ className = "", variant = "mobile" }) {
  const { user } = useAuth();
  const { status, supported, busy, subscribe } = usePushSubscription({ enabled: !!user });
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    // Delay 1.2s on mount so the banner doesn't crash the initial paint —
    // gives the LCP a chance to complete first.
    const t = setTimeout(() => setDismissed(wasDismissedRecently()), 1200);
    return () => clearTimeout(t);
  }, []);

  const shouldShow =
    !!user &&
    supported &&
    status === "unsubscribed" &&
    !dismissed;

  if (!shouldShow) return null;

  const handleEnable = async () => {
    const r = await subscribe();
    if (r?.ok) {
      toast.success("Push alerts on — you'll know the moment your favorites go live");
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* fine */ }
      setDismissed(true);
    } else if (r?.reason === "denied") {
      toast.error("Push denied — enable it in Safari → Settings → Notifications");
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* fine */ }
      setDismissed(true);
    } else if (r?.reason === "unsupported") {
      // iOS Safari before 16.4 or install-to-home-screen not done.
      toast.info("On iPhone: tap the Share icon → Add to Home Screen, then re-enable");
    }
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* fine */ }
    setDismissed(true);
  };

  if (variant === "compact") {
    return (
      <button
        data-testid="push-prompt-compact"
        onClick={handleEnable}
        disabled={busy}
        className={`inline-flex items-center gap-1.5 rounded-full bg-yellow-400 text-black text-[11px] font-black uppercase tracking-wider px-3 py-1.5 active:scale-95 transition-transform disabled:opacity-50 ${className}`}
      >
        <Bell className="h-3 w-3" />
        Get live alerts
      </button>
    );
  }

  return (
    <div
      data-testid="push-prompt-banner"
      className={`fixed z-40 left-3 right-3 bottom-40 md:hidden bg-gradient-to-r from-red-600 to-pink-600 rounded-2xl shadow-2xl shadow-red-900/40 p-3 flex items-center gap-3 ${className}`}
    >
      <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
        <Bell className="h-5 w-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-black text-sm leading-tight">Never miss a go-live</div>
        <div className="text-white/80 text-[11px] leading-tight mt-0.5">Get a ping the moment your favourite creators start streaming.</div>
      </div>
      <button
        data-testid="push-prompt-enable"
        onClick={handleEnable}
        disabled={busy}
        className="bg-white text-red-600 font-black text-[11px] uppercase tracking-wider rounded-full px-3 py-1.5 disabled:opacity-50 active:scale-95 transition-transform"
      >
        {busy ? "…" : "Enable"}
      </button>
      <button
        data-testid="push-prompt-dismiss"
        onClick={handleDismiss}
        className="h-7 w-7 rounded-full flex items-center justify-center text-white/70 hover:text-white flex-shrink-0"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
